import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { crmApi } from "@/lib/ownerClient";

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-white">{value}</div>
      {hint && <div className="mt-1 text-xs text-zinc-400">{hint}</div>}
    </div>
  );
}

export default function OwnerOverview() {
  const { ctx } = useOutletContext();
  const [agents, setAgents] = useState([]);
  const [stripe, setStripe] = useState(null);
  const [audit, setAudit] = useState({ count: 0, events: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, s, evt] = await Promise.all([
          crmApi.listAgents(),
          crmApi.stripeStatus(),
          crmApi.audit({ limit: 8 }),
        ]);
        setAgents(a || []);
        setStripe(s);
        setAudit(evt);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="owner-overview">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">CRM Overview</h1>
        <p className="text-sm text-zinc-400 mt-1">
          Phase A foundation. Tenant <span className="text-white font-medium">{ctx?.tenant?.display_name || ctx?.tenant_id}</span> is active. Lead-gen workflows light up in Phase B.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Agents Online" value={agents.length} hint="All 11 personas seeded" />
        <Stat label="Role" value={ctx?.role || "owner"} hint={`${(ctx?.permissions || []).length} permissions`} />
        <Stat
          label="Stripe Mode"
          value={stripe?.mode || "unconfigured"}
          hint={stripe?.configured ? "test keys live" : "set STRIPE_SECRET_KEY=sk_test_… to enable"}
        />
        <Stat label="Audit Events" value={audit?.count ?? 0} hint="last 200 visible" />
      </div>

      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-base text-white">Agent Swarm</CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {agents.map((a) => (
            <div key={a.slug} className="rounded-md border border-zinc-800 bg-black/60 p-3" data-testid={`agent-card-${a.slug}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium text-white">{a.display_name}</div>
                <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px]">
                  {a.role}
                </Badge>
              </div>
              <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{a.duties}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-base text-white">Recent Compliance Audit Events</CardTitle>
        </CardHeader>
        <CardContent>
          {audit.events.length === 0 ? (
            <div className="text-sm text-zinc-500">No audit events yet.</div>
          ) : (
            <ul className="divide-y divide-zinc-800 text-sm">
              {audit.events.slice(0, 8).map((e) => (
                <li key={e.id} className="py-2 flex items-center justify-between gap-3" data-testid={`audit-row-${e.id}`}>
                  <div className="min-w-0">
                    <div className="text-white truncate">{e.action}</div>
                    <div className="text-[11px] text-zinc-500">
                      {e.actor_type} · {e.actor_id} · {new Date(e.created_at).toLocaleString()}
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      e.outcome === "pass"
                        ? "border-emerald-500/40 text-emerald-300"
                        : e.outcome === "fail"
                          ? "border-rose-500/40 text-rose-300"
                          : "border-zinc-700 text-zinc-400"
                    }
                  >
                    {e.outcome}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
